import { useCallback, useEffect, useRef, useState } from "react";
import type { PlayerSnapshot } from "../types";

/**
 * PhotoMode — an in-browser snapshot camera.
 *
 * Press "P" (or tap the shutter button) to grab a clean, HUD-free frame of the
 * live Gaussian-splat scene. Each shot gets a subtle "postcard" telemetry stamp
 * burned into the corner (world, altitude, speed, heading, timestamp), a camera
 * flash, and lands in an in-session filmstrip you can re-download or discard.
 *
 * Self-contained: it owns its gallery state and reads the WebGL canvas straight
 * from the DOM. The R3F <Canvas> runs with preserveDrawingBuffer enabled, so the
 * live frame can be drawn onto a 2D canvas synchronously — no MediaStream timing
 * to fight — where the telemetry stamp is composited before export.
 */

export interface PhotoShot {
  id: string;
  url: string;
  label: string;
  time: string;
}

interface PhotoModeProps {
  /** Whether the shutter control should be shown (typically once the scene is ready). */
  active: boolean;
  /** Latest player telemetry — burned into the stamp. */
  player: PlayerSnapshot;
  /** Human-readable world/floor label for the stamp. */
  worldLabel?: string;
  /** Optional toast callback so captures announce themselves in the game's own voice. */
  onFlash?: (message: string) => void;
}

const MAX_SHOTS = 8;
const APP_CANVAS_SELECTOR = ".app-shell canvas";

function pad(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}

function stampTime(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

function fileStamp(date: Date): string {
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(
    date.getHours(),
  )}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
}

/**
 * Give the continuously-rendering R3F loop a moment to paint a fresh frame into
 * the preserved buffer before we read it. A short timer (not requestAnimationFrame,
 * which throttles hard when the tab isn't foreground) keeps this reliable both in
 * real browsers and in headless capture.
 */
function settle(ms = 40): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function encode(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

function headingLabel(yaw: number): string {
  const deg = ((-yaw * 180) / Math.PI + 360) % 360;
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const dir = dirs[Math.round(deg / 45) % 8];
  return `${Math.round(deg)}° ${dir}`;
}

/** Burn a subtle telemetry postcard bar across the bottom of the frame. */
function stampFrame(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  world: string,
  telemetry: string,
  time: string,
) {
  const barHeight = Math.max(56, Math.round(height * 0.11));
  const gradient = ctx.createLinearGradient(0, height - barHeight, 0, height);
  gradient.addColorStop(0, "rgba(4, 12, 9, 0)");
  gradient.addColorStop(1, "rgba(4, 12, 9, 0.72)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, height - barHeight, width, barHeight);

  const pad = Math.round(width * 0.028);
  const titleSize = Math.max(15, Math.round(height * 0.026));
  const subSize = Math.max(12, Math.round(height * 0.02));
  const baseY = height - Math.round(barHeight * 0.34);

  ctx.textBaseline = "alphabetic";
  ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
  ctx.shadowBlur = 6;

  // Left: brand + world.
  ctx.textAlign = "left";
  ctx.fillStyle = "#4cff76";
  ctx.font = `600 ${titleSize}px ui-sans-serif, system-ui, -apple-system, sans-serif`;
  ctx.fillText("MYWORLD FLIGHT", pad, baseY - subSize - 4);
  ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
  ctx.font = `500 ${subSize}px ui-sans-serif, system-ui, -apple-system, sans-serif`;
  ctx.fillText(world, pad, baseY);

  // Right: telemetry + time.
  ctx.textAlign = "right";
  ctx.fillStyle = "#61cfff";
  ctx.font = `600 ${subSize}px ui-monospace, SFMono-Regular, Menlo, monospace`;
  ctx.fillText(telemetry, width - pad, baseY - subSize - 4);
  ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
  ctx.font = `500 ${Math.max(11, subSize - 2)}px ui-monospace, SFMono-Regular, Menlo, monospace`;
  ctx.fillText(time, width - pad, baseY);

  ctx.shadowBlur = 0;
}

export function PhotoMode({ active, player, worldLabel, onFlash }: PhotoModeProps) {
  const [shots, setShots] = useState<PhotoShot[]>([]);
  const [flashing, setFlashing] = useState(false);
  const [busy, setBusy] = useState(false);
  const shotsRef = useRef<PhotoShot[]>([]);
  // Keep a live snapshot of telemetry so the keyboard handler can read fresh values.
  const telemetryRef = useRef({ player, worldLabel });
  telemetryRef.current = { player, worldLabel };

  useEffect(() => {
    shotsRef.current = shots;
  }, [shots]);

  const capture = useCallback(async () => {
    if (busy) return;
    const canvas = document.querySelector<HTMLCanvasElement>(APP_CANVAS_SELECTOR);
    if (!canvas || typeof canvas.toBlob !== "function") {
      onFlash?.("Photo capture unavailable");
      return;
    }
    setBusy(true);
    setFlashing(true);
    window.setTimeout(() => setFlashing(false), 320);

    const now = new Date();
    const { player: snap, worldLabel: world } = telemetryRef.current;
    const worldText = world && world.trim() ? world : "Uncharted world";
    const telemetry = `ALT ${Math.round(snap.y)}  SPD ${Math.round(snap.speed)}  ${headingLabel(
      snap.yaw,
    )}`;

    // Draw the WebGL buffer onto a 2D canvas, stamp it, and PNG-encode it.
    // The R3F <Canvas> runs with preserveDrawingBuffer, so the buffer is directly
    // drawable. Any single attempt can miss — an empty preserved buffer on the very
    // first grab, or a transient WebGL context-loss (GPU reset / tab backgrounding)
    // that makes drawImage throw — so we retry a couple of times with a short wait.
    const grabBlob = async (): Promise<Blob | null> => {
      await settle();
      const width = canvas.width || 1280;
      const height = canvas.height || 720;
      const out = document.createElement("canvas");
      out.width = width;
      out.height = height;
      const ctx = out.getContext("2d");
      if (!ctx) throw new Error("no 2d context");
      ctx.drawImage(canvas, 0, 0, width, height);
      stampFrame(ctx, width, height, worldText, telemetry, stampTime(now));
      return encode(out);
    };

    try {
      let blob: Blob | null = null;
      for (let attempt = 0; attempt < 3 && !blob; attempt += 1) {
        try {
          blob = await grabBlob();
        } catch {
          /* transient (e.g. context lost) — wait and retry below */
        }
        if (!blob) await settle(120);
      }
      if (!blob) throw new Error("capture failed");

      const url = URL.createObjectURL(blob);
      const shot: PhotoShot = {
        id: `${now.getTime()}-${Math.random().toString(36).slice(2, 7)}`,
        url,
        label: fileStamp(now),
        time: stampTime(now),
      };
      setShots((current) => {
        const next = [shot, ...current];
        const trimmed = next.slice(0, MAX_SHOTS);
        next.slice(MAX_SHOTS).forEach((old) => URL.revokeObjectURL(old.url));
        return trimmed;
      });
      onFlash?.("Photo captured");
    } catch {
      onFlash?.("Photo capture failed");
    } finally {
      setBusy(false);
    }
  }, [busy, onFlash]);

  const download = useCallback((shot: PhotoShot) => {
    const link = document.createElement("a");
    link.href = shot.url;
    link.download = `myworld-flight-${shot.label}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);

  const remove = useCallback((id: string) => {
    setShots((current) => {
      const target = current.find((shot) => shot.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return current.filter((shot) => shot.id !== id);
    });
  }, []);

  // Keyboard shortcut: "P" snaps a photo (ignored while typing in a field).
  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.repeat || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key !== "p" && event.key !== "P") return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) {
        return;
      }
      event.preventDefault();
      void capture();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, capture]);

  // Release any outstanding object URLs on unmount.
  useEffect(() => {
    return () => {
      shotsRef.current.forEach((shot) => URL.revokeObjectURL(shot.url));
    };
  }, []);

  if (!active) return null;

  return (
    <>
      {flashing && <div className="photo-flash" aria-hidden="true" />}
      <button
        type="button"
        className={`photo-shutter ${busy ? "is-busy" : ""}`}
        onClick={() => void capture()}
        disabled={busy}
        title="Snap a photo (P)"
        aria-label="Snap a photo"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path
            fill="currentColor"
            d="M9.4 4h5.2l1.3 1.8H19a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7.8a2 2 0 0 1 2-2h3.1L9.4 4Zm2.6 4.6a4.6 4.6 0 1 0 0 9.2 4.6 4.6 0 0 0 0-9.2Zm0 2a2.6 2.6 0 1 1 0 5.2 2.6 2.6 0 0 1 0-5.2Z"
          />
        </svg>
      </button>

      {shots.length > 0 && (
        <div className="photo-filmstrip" role="list" aria-label="Captured photos">
          <div className="photo-filmstrip-head">
            <span>Snapshots</span>
            <span className="photo-count">{shots.length}</span>
          </div>
          <div className="photo-strip-scroll">
            {shots.map((shot) => (
              <div key={shot.id} className="photo-thumb" role="listitem">
                <img src={shot.url} alt={`Snapshot ${shot.time}`} />
                <div className="photo-thumb-actions">
                  <button
                    type="button"
                    className="photo-thumb-btn"
                    title="Download PNG"
                    aria-label={`Download snapshot ${shot.time}`}
                    onClick={() => download(shot)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="photo-thumb-btn is-remove"
                    title="Discard"
                    aria-label={`Discard snapshot ${shot.time}`}
                    onClick={() => remove(shot.id)}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
